import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import type {
  VoiceApiCommand,
  VoiceEventResponse,
  VoiceScreen,
  VoiceSessionState,
} from "@ghalishor/voice-core";
import { CustomerVoiceExpertV3Client } from "./customer-voice-expert-v3-client";
import {
  VOICE_MAX_RECORDING_MS,
  advanceVoiceSilence,
  createVoiceSilenceState,
} from "./voice-silence-detector";
import {
  readCustomerVoicePreference,
  setCustomerVoicePreference,
  subscribeCustomerVoicePreference,
} from "./customer-voice-preference";

type SpeakCommand = Extract<VoiceApiCommand, { readonly type: "speak" }>;

let sharedVoiceAudio: HTMLAudioElement | null = null;

function getSharedVoiceAudio(): HTMLAudioElement {
  if (!sharedVoiceAudio) {
    sharedVoiceAudio = new Audio();
    sharedVoiceAudio.preload = "auto";
    sharedVoiceAudio.setAttribute("playsinline", "");
  }
  return sharedVoiceAudio;
}

function latestSpeech(response: VoiceEventResponse): SpeakCommand | null {
  return (
    [...response.commands]
      .reverse()
      .find((command): command is SpeakCommand => command.type === "speak") ??
    null
  );
}

export function playVoiceAudioToEnd(
  audio: HTMLAudioElement,
  timeoutMs: number,
): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (played: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      audio.onended = null;
      audio.onerror = null;
      audio.onpause = null;
      resolve(played);
    };
    const timer = window.setTimeout(() => finish(false), timeoutMs);
    audio.onended = () => finish(true);
    audio.onerror = () => finish(false);
    audio.onpause = () => finish(false);
    void audio.play().catch(() => finish(false));
  });
}

const RECORDING_TYPES = ["audio/webm", "audio/mp4", "audio/ogg"] as const;
type RecordingMimeType = (typeof RECORDING_TYPES)[number];

function recordingType(): RecordingMimeType | null {
  if (typeof MediaRecorder === "undefined") return null;
  return (
    RECORDING_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) ?? null
  );
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("VOICE_AUDIO_READ_FAILED"));
    reader.onload = () => {
      const value = typeof reader.result === "string" ? reader.result : "";
      resolve(value.slice(value.indexOf(",") + 1));
    };
    reader.readAsDataURL(blob);
  });
}

export function CustomerVoiceExpertV3(props: {
  readonly client: CustomerVoiceExpertV3Client;
  readonly screen: VoiceScreen;
  readonly authenticated: boolean;
  readonly autoSpeak?: boolean;
  readonly permissionPrompt?: boolean;
  readonly onPlay?: () => void;
  readonly orderInput?: boolean;
  readonly busy?: boolean;
  readonly onOrderState?: (state: VoiceSessionState) => void;
  readonly onTranscribe?: (
    audioBase64: string,
    audioMimeType: "audio/webm" | "audio/mp4" | "audio/ogg",
  ) => Promise<string>;
}) {
  const voicePreference = useSyncExternalStore(
    subscribeCustomerVoicePreference,
    readCustomerVoicePreference,
    readCustomerVoicePreference,
  );
  const [speech, setSpeech] = useState<SpeakCommand | null>(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [working, setWorking] = useState(false);
  const [listening, setListening] = useState(false);
  const [processingVoice, setProcessingVoice] = useState(false);
  const [choices, setChoices] = useState<readonly string[]>([]);
  const [lastTranscript, setLastTranscript] = useState("");
  const [orderState, setOrderState] =
    useState<VoiceSessionState["order"]>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recordingTimerRef = useRef<number | null>(null);
  const analysisFrameRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const onPlayRef = useRef(props.onPlay);
  const onOrderStateRef = useRef(props.onOrderState);

  useEffect(() => {
    onPlayRef.current = props.onPlay;
  }, [props.onPlay]);

  useEffect(() => {
    onOrderStateRef.current = props.onOrderState;
  }, [props.onOrderState]);

  const stopAudioAnalysis = useCallback(() => {
    if (analysisFrameRef.current !== null)
      window.cancelAnimationFrame(analysisFrameRef.current);
    analysisFrameRef.current = null;
    const context = audioContextRef.current;
    audioContextRef.current = null;
    if (context && context.state !== "closed")
      void context.close().catch(() => undefined);
  }, []);

  const play = useCallback(
    async (command: SpeakCommand | null): Promise<boolean> => {
      if (!command?.audioBase64 || !command.audioMimeType) return false;
      const previous = audioRef.current;
      if (previous) {
        previous.onpause = null;
        previous.pause();
      }
      const audio = getSharedVoiceAudio();
      audio.onended = null;
      audio.onerror = null;
      audio.onpause = null;
      audio.src = `data:${command.audioMimeType};base64,${command.audioBase64}`;
      audio.volume = 1;
      audio.playbackRate = 1.12;
      audio.preservesPitch = true;
      audio.currentTime = 0;
      audio.load();
      audioRef.current = audio;
      const played = await playVoiceAudioToEnd(
        audio,
        Math.min(120_000, Math.max(12_000, command.text.length * 240)),
      );
      if (audioRef.current === audio) audioRef.current = null;
      return played;
    },
    [],
  );

  const acceptResponse = useCallback(
    async (response: VoiceEventResponse, shouldPlay: boolean) => {
      const command = latestSpeech(response);
      setSpeech(command);
      setChoices(
        response.commands.flatMap((item) =>
          item.type === "show_choices" ? item.choices : [],
        ),
      );
      setOrderState(response.state.order);
      onOrderStateRef.current?.(response.state);
      if (!shouldPlay || readCustomerVoicePreference() !== "enabled") return;
      setPlaying(true);
      const played = await play(command);
      setPlaying(false);
      if (played) onPlayRef.current?.();
    },
    [play],
  );

  const requestReplay = useCallback(async () => {
    if (playing) return;
    setPlaying(true);
    setError(null);
    try {
      const response = await props.client.replay(props.authenticated);
      const command = latestSpeech(response);
      setSpeech(command);
      setChoices(
        response.commands.flatMap((item) =>
          item.type === "show_choices" ? item.choices : [],
        ),
      );
      setOrderState(response.state.order);
      onOrderStateRef.current?.(response.state);
      const played = await play(command);
      if (played) onPlayRef.current?.();
      else setError("صدا آماده نشد. لطفاً دوباره تلاش کنید.");
    } catch {
      setError("ارتباط با کارشناس صوتی برقرار نشد.");
    } finally {
      setPlaying(false);
    }
  }, [play, playing, props.authenticated, props.client]);

  const stopAndMute = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      audioRef.current = null;
    }
    setPlaying(false);
    setCustomerVoicePreference("muted");
  }, []);

  const enableAndReplay = useCallback(async () => {
    setCustomerVoicePreference("enabled");
    if (!speech?.audioBase64 || !speech.audioMimeType) {
      await requestReplay();
      return;
    }
    setPlaying(true);
    setError(null);
    const played = await play(speech);
    setPlaying(false);
    if (played) onPlayRef.current?.();
    else setError("پخش صدا توسط مرورگر متوقف شد؛ دوباره دکمه پخش را بزنید.");
  }, [play, requestReplay, speech]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void props.client
      .enter(props.screen, props.authenticated)
      .then(async (response) => {
        if (cancelled) return;
        const command = latestSpeech(response);
        setSpeech(command);
        setChoices(
          response.commands.flatMap((item) =>
            item.type === "show_choices" ? item.choices : [],
          ),
        );
        setOrderState(response.state.order);
        onOrderStateRef.current?.(response.state);
        setLoading(false);
        if (
          props.autoSpeak === false ||
          readCustomerVoicePreference() !== "enabled"
        )
          return;
        setPlaying(true);
        const played = await play(command);
        if (cancelled) return;
        setPlaying(false);
        if (played) onPlayRef.current?.();
      })
      .catch(() => {
        if (cancelled) return;
        setLoading(false);
        setError("ارتباط با کارشناس صوتی برقرار نشد.");
      });
    return () => {
      cancelled = true;
      audioRef.current?.pause();
      audioRef.current = null;
      recorderRef.current?.stop();
      streamRef.current?.getTracks().forEach((track) => track.stop());
      if (recordingTimerRef.current !== null)
        window.clearTimeout(recordingTimerRef.current);
      stopAudioAnalysis();
    };
  }, [
    play,
    props.authenticated,
    props.autoSpeak,
    props.client,
    props.screen,
    stopAudioAnalysis,
  ]);

  const submitAnswer = useCallback(
    async (raw: string) => {
      const transcript = raw.trim();
      if (!transcript || working || props.busy) return;
      setWorking(true);
      setError(null);
      setInput("");
      setLastTranscript(transcript);
      try {
        const response = await props.client.answer(transcript);
        await acceptResponse(response, true);
      } catch {
        setError("پاسخ شما دریافت نشد. لطفاً یک‌بار دیگر بگویید.");
      } finally {
        setWorking(false);
      }
    },
    [acceptResponse, props.busy, props.client, working],
  );

  const stopListening = useCallback(() => {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  }, []);

  const startListening = useCallback(async () => {
    if (!props.onTranscribe || working || props.busy) return;
    const mimeType = recordingType();
    if (!mimeType || !navigator.mediaDevices?.getUserMedia) {
      setError("ضبط صدا در این مرورگر در دسترس نیست؛ پاسخ را بنویسید.");
      return;
    }
    setError(null);
    setProcessingVoice(false);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (cause) {
      const permissionDenied =
        cause instanceof DOMException &&
        (cause.name === "NotAllowedError" || cause.name === "SecurityError");
      setError(
        permissionDenied
          ? "اجازهٔ میکروفن داده نشد؛ پاسخ را بنویسید."
          : "میکروفن شروع نشد؛ دوباره تلاش کنید یا پاسخ را بنویسید.",
      );
      return;
    }
    try {
      const chunks: BlobPart[] = [];
      const recorder = new MediaRecorder(stream, { mimeType });
      streamRef.current = stream;
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };
      recorder.onstop = () => {
        setListening(false);
        setProcessingVoice(true);
        stopAudioAnalysis();
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        recorderRef.current = null;
        if (recordingTimerRef.current !== null)
          window.clearTimeout(recordingTimerRef.current);
        recordingTimerRef.current = null;
        const blob = new Blob(chunks, { type: mimeType });
        if (blob.size === 0) {
          setProcessingVoice(false);
          setError("صدایی دریافت نشد؛ دوباره تلاش کنید.");
          return;
        }
        setWorking(true);
        void blobToBase64(blob)
          .then((encoded) => props.onTranscribe?.(encoded, mimeType))
          .then((transcript) =>
            transcript ? submitAnswer(transcript) : undefined,
          )
          .catch(() =>
            setError("صدا واضح دریافت نشد؛ پاسخ را کوتاه‌تر تکرار کنید."),
          )
          .finally(() => {
            setProcessingVoice(false);
            setWorking(false);
          });
      };
      recorder.start();
      setListening(true);
    } catch {
      stream.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      recorderRef.current = null;
      setListening(false);
      setError("میکروفن شروع نشد؛ دوباره تلاش کنید یا پاسخ را بنویسید.");
      return;
    }
    try {
      const AudioContextConstructor =
        window.AudioContext ??
        (window as Window & { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (AudioContextConstructor) {
        const context = new AudioContextConstructor();
        const analyser = context.createAnalyser();
        analyser.fftSize = 512;
        context.createMediaStreamSource(stream).connect(analyser);
        audioContextRef.current = context;
        const samples = new Float32Array(analyser.fftSize);
        let silence = createVoiceSilenceState(performance.now());
        const inspectLevel = (nowMs: number) => {
          analyser.getFloatTimeDomainData(samples);
          let energy = 0;
          for (const sample of samples) energy += sample * sample;
          const result = advanceVoiceSilence(
            silence,
            Math.sqrt(energy / samples.length),
            nowMs,
          );
          silence = result.state;
          if (result.shouldStop) {
            stopListening();
            return;
          }
          analysisFrameRef.current = window.requestAnimationFrame(inspectLevel);
        };
        analysisFrameRef.current = window.requestAnimationFrame(inspectLevel);
      }
    } catch {
      stopAudioAnalysis();
    }
    recordingTimerRef.current = window.setTimeout(
      () => stopListening(),
      VOICE_MAX_RECORDING_MS,
    );
  }, [
    props.busy,
    props.onTranscribe,
    stopAudioAnalysis,
    stopListening,
    submitAnswer,
    working,
  ]);

  return (
    <section
      className={`profile-voice-guide${props.orderInput ? " profile-voice-guide--interactive" : ""}${playing ? " profile-voice-guide--playing" : ""}${props.permissionPrompt && voicePreference === "ask" ? " profile-voice-guide--permission" : ""}`}
      aria-live="polite"
    >
      <div className="profile-voice-guide__avatar" aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
      </div>
      <div className="profile-voice-guide__content">
        <strong>کارشناس همراه قالیشور</strong>
        <p>
          {props.orderInput
            ? "فقط یک سؤال در هر مرحله؛ بدون تکرار بی‌مورد"
            : props.permissionPrompt && voicePreference === "ask"
              ? "می‌توانید معرفی کوتاه را بشنوید، یا مستقیم سفارش را شروع کنید."
              : (speech?.text ??
                (loading
                  ? "در حال آماده‌سازی راهنما…"
                  : "راهنمای این مرحله آماده است."))}
        </p>
        {error ? <small role="status">{error}</small> : null}
      </div>
      {props.permissionPrompt && voicePreference === "ask" ? (
        <div className="profile-voice-guide__permission" role="status">
          <strong>مایلید راهنمای صوتی صحبت کند؟</strong>
          <small>تا اجازه ندهید صدایی پخش نمی‌شود.</small>
        </div>
      ) : voicePreference === "muted" ? (
        <div className="profile-voice-guide__muted" role="status">
          راهنمای صوتی بی‌صداست و فقط با انتخاب شما دوباره فعال می‌شود.
        </div>
      ) : null}
      <div className="profile-voice-guide__controls">
        <button
          type="button"
          className={
            playing
              ? "voice-guide-stop"
              : props.permissionPrompt && voicePreference === "ask"
                ? "voice-guide-allow"
                : "secondary"
          }
          onClick={playing ? stopAndMute : () => void enableAndReplay()}
          disabled={loading}
          aria-pressed={voicePreference === "enabled"}
        >
          {playing
            ? "ساکت کردن کارشناس"
            : voicePreference === "ask"
              ? props.permissionPrompt
                ? "اجازه می‌دهم؛ پخش معرفی"
                : "اجازه می‌دهم؛ پخش راهنما"
              : voicePreference === "muted"
                ? "فعال کردن و پخش راهنما"
                : "پخش دوباره راهنمای این مرحله"}
        </button>
        {props.permissionPrompt && voicePreference === "ask" ? (
          <button
            type="button"
            className="link-button voice-guide-decline"
            onClick={() => setCustomerVoicePreference("muted")}
          >
            فعلاً بی‌صدا ادامه می‌دهم
          </button>
        ) : null}
      </div>
      {props.orderInput ? (
        <div className="order-ai-panel">
          <section className="order-voice-question" aria-live="polite">
            <h2>{speech?.text ?? "چه چیزی برای شست‌وشو دارید؟"}</h2>
            {!lastTranscript ? (
              <p>مثلاً بگویید: «دو فرش ماشینی دوازده متری دارم»</p>
            ) : null}
          </section>
          <div
            className={`order-voice-listener${listening ? " listening" : ""}${processingVoice ? " processing" : ""}`}
          >
            <span className="order-voice-listener__ring" aria-hidden="true" />
            <button
              type="button"
              className="order-voice-listener__button"
              onClick={listening ? stopListening : () => void startListening()}
              disabled={working || props.busy || processingVoice}
              aria-label={listening ? "پایان پاسخ صوتی" : "شروع پاسخ صوتی"}
            >
              {listening ? "■" : "●"}
            </button>
          </div>
          <div className="order-voice-listener__status" aria-live="polite">
            <strong>
              {listening
                ? "در حال شنیدن…"
                : processingVoice
                  ? "در حال بررسی پاسخ…"
                  : "برای صحبت لمس کنید"}
            </strong>
            <small>
              {listening
                ? "صحبت کنید؛ با مکث شما خودکار ادامه می‌دهم."
                : "پس از سه ثانیه مکث، پاسخ خودکار ثبت می‌شود."}
            </small>
          </div>
          {listening || processingVoice ? (
            <div
              className={
                listening
                  ? "order-ai-listening-status active"
                  : "order-ai-listening-status"
              }
              role="status"
            >
              <span aria-hidden="true" />
              {listening
                ? "در حال شنیدن سفارش شما… پس از ۳ ثانیه مکث، پاسخ خودکار ارسال می‌شود."
                : "پایان صحبت تشخیص داده شد؛ در حال بررسی پاسخ شما…"}
            </div>
          ) : null}
          <section className="order-ai-transcript" aria-label="متن دریافت‌شده">
            <span>آنچه شنیدم</span>
            <p>{lastTranscript || "هنوز پاسخی دریافت نشده است."}</p>
          </section>
          {orderState?.itemLabel && orderState.itemCount !== null ? (
            <section className="order-ai-understood" aria-live="polite">
              <header>
                <span aria-hidden="true">✓</span>
                <strong>درست متوجه شدم</strong>
              </header>
              <p>
                {orderState.itemCount.toLocaleString("fa-IR")} عدد{" "}
                {orderState.itemLabel}
                {orderState.measurementStatus === "onsite"
                  ? " · اندازه‌گیری هنگام تحویل"
                  : orderState.areaPerItemSqm !== null
                    ? ` · هرکدام ${orderState.areaPerItemSqm.toLocaleString("fa-IR")} مترمربع`
                    : ""}
              </p>
            </section>
          ) : null}
          {choices.length > 0 ? (
            <div
              className="order-ai-choices"
              role="group"
              aria-label="پاسخ‌های پیشنهادی"
            >
              {choices.map((choice) => (
                <button
                  type="button"
                  key={choice}
                  onClick={() => void submitAnswer(choice)}
                  disabled={working || props.busy}
                >
                  {choice}
                </button>
              ))}
            </div>
          ) : null}
          <form
            className="order-ai-composer"
            onSubmit={(event) => {
              event.preventDefault();
              void submitAnswer(input);
            }}
          >
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder={
                listening ? "در حال شنیدن…" : "پاسخ را بگویید یا بنویسید"
              }
              disabled={working || props.busy}
            />
            <button
              type="submit"
              className="order-ai-send"
              disabled={!input.trim() || working || props.busy}
            >
              ارسال
            </button>
          </form>
        </div>
      ) : null}
    </section>
  );
}
